// =============================================================================
// Funnel Landing Page  —  route: /
// FB Ad -> THIS PAGE -> /apply -> /result
//
// A selective, premium application landing page — positioned as a commission-
// based business opportunity, NOT a travel-agent job listing.
// =============================================================================

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FOUNDER_VIDEO_URL, COHORT } from './funnelConfig';
import { captureAttribution, trackEvent } from './tracking';

const APPLY_PATH = '/apply';

const lookingFor = [
  {
    title: 'Entrepreneurial mindset',
    body: 'You want to build something of your own and take full ownership of the outcome.',
  },
  {
    title: 'Sales or relationship-building ability',
    body: 'You build trust naturally and can guide people toward a confident decision.',
  },
  {
    title: 'Comfortable with commission-based income',
    body: 'You are motivated by results and earnings you control — not a fixed paycheque.',
  },
  {
    title: 'Coachable and growth-minded',
    body: 'You follow proven systems and mentorship instead of guessing your way through.',
  },
  {
    title: 'Willing to generate leads',
    body: 'You will build a client base through relationships, referrals, content, and outreach.',
  },
  {
    title: 'Consistent follow-up habits',
    body: 'You stay organized and follow through — high-ticket travel is won in the follow-up.',
  },
  {
    title: 'Interest in luxury and cruise travel',
    body: 'You are genuinely drawn to high-ticket, premium travel experiences and clients.',
  },
];

const notFor = [
  'You want a guaranteed salary and fixed hours.',
  'You only want travel perks or discounts.',
  'You see this as a hobby rather than a business.',
  'You are not willing to generate your own leads.',
  'You are uncomfortable with sales conversations.',
  'You expect clients to be handed to you.',
];

const successfulHave = [
  'An existing network or community to start from.',
  'Comfort having sales conversations.',
  'Self-discipline and week-to-week consistency.',
  'A willingness to build a personal client base over time.',
];

const peopleStruggle = [
  'They expect guaranteed income.',
  'They avoid follow-up.',
  'They dislike outreach.',
  'They only want access to travel perks.',
];

const whatYouBuild = [
  {
    title: 'A luxury travel client base',
    body: 'A book of high-ticket clients booking cruises, tours, and premium getaways under the Revolution Travel brand.',
  },
  {
    title: 'A relationship-driven referral business',
    body: 'Repeat clients and referrals that compound — the foundation of a durable travel business.',
  },
  {
    title: 'High-ticket cruise & travel sales experience',
    body: 'Real skill selling premium and luxury travel, mentored by an award-winning advisor.',
  },
  {
    title: 'Supplier partnerships',
    body: 'Direct access to cruise lines, tour operators, and business development managers.',
  },
  {
    title: 'Systems, mentorship & support',
    body: 'A proven CRM and booking systems, weekly coaching, and one-on-one guidance.',
  },
  {
    title: 'Flexible business ownership',
    body: 'You own your schedule and your income — a real business built on your terms.',
  },
];

const founderProofPoints = [
  { title: '$2M+', subtitle: 'in personal travel sales' },
  { title: '3x Top 100', subtitle: 'Virgin Voyages North American Advisor' },
  { title: 'Only Canadian', subtitle: 'to achieve that distinction' },
  { title: 'Award Recipient', subtitle: 'Royal Caribbean "Best Growth"' },
  { title: 'Luxury & Cruise', subtitle: 'Travel Specialist' },
];

const applicationSteps = [
  { title: 'Read the opportunity', body: 'Understand what this is — a selective business opportunity, not a job.' },
  { title: 'Complete the application', body: 'A short application — about 5 minutes. Tell us about your background and goals.' },
  { title: 'Get automatically assessed', body: 'Your answers are scored against the entrepreneurial profile we look for.' },
  {
    title: 'Qualified applicants submit a resume & cover letter',
    body: 'Strong-fit applicants are asked for a resume, a short cover letter, and a brief client plan.',
  },
  { title: 'The team reviews before any conversation', body: 'We review every qualified application before reaching out.' },
];

function PrimaryCTA({ children, className = '', location }) {
  return (
    <Link
      to={APPLY_PATH}
      onClick={() => trackEvent('cta_apply_click', { cta_location: location })}
      className={`inline-flex items-center justify-center rounded-lg bg-revolution-orange px-8 py-4 text-base font-bold text-white shadow-lg transition hover:bg-orange-600 ${className}`}
    >
      {children}
      <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
    </Link>
  );
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

const FOUNDER_VIDEO_TITLE = 'Watch this before you apply';
const VIDEO_PROGRESS_MILESTONES = [25, 50, 75];

/** "Watch This Before You Apply" — founder video, or a styled placeholder. */
function FounderVideo() {
  const isFile = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(FOUNDER_VIDEO_URL);
  const isEmbed = /^https?:\/\//i.test(FOUNDER_VIDEO_URL);
  const videoRef = useRef(null);
  // Native <video> only. Iframe embeds (YouTube/Vimeo) need the provider JS
  // API for play/progress events — wire that up separately if we ever switch
  // FOUNDER_VIDEO_URL to an embed.
  useEffect(() => {
    if (!isFile) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    let playFired = false;
    const milestonesFired = new Set();
    const handlePlay = () => {
      if (playFired) return;
      playFired = true;
      trackEvent('video_play', {
        video_title: FOUNDER_VIDEO_TITLE,
        video_duration: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
      });
    };
    const handleTimeUpdate = () => {
      const duration = el.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const percent = (el.currentTime / duration) * 100;
      VIDEO_PROGRESS_MILESTONES.forEach((m) => {
        if (percent >= m && !milestonesFired.has(m)) {
          milestonesFired.add(m);
          trackEvent('video_progress', { video_title: FOUNDER_VIDEO_TITLE, percent: m });
        }
      });
    };
    const handleEnded = () => {
      trackEvent('video_complete', {
        video_title: FOUNDER_VIDEO_TITLE,
        video_duration: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
      });
    };
    el.addEventListener('play', handlePlay);
    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('ended', handleEnded);
    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('timeupdate', handleTimeUpdate);
      el.removeEventListener('ended', handleEnded);
    };
  }, [isFile]);
  return (
    <div className="mx-auto mt-12 max-w-3xl">
      <h3 className="text-center text-2xl font-extrabold text-revolution-navy sm:text-3xl">
        Watch This Before You Apply
      </h3>
      <p className="mx-auto mt-2 max-w-xl text-center text-gray-600">
        A short message from Sean on who this opportunity is — and isn&rsquo;t — for.
      </p>
      <div className="mt-6 overflow-hidden rounded-2xl bg-revolution-navy shadow-xl">
        {isFile ? (
          <video
            ref={videoRef}
            src={FOUNDER_VIDEO_URL}
            className="aspect-video w-full"
            controls
            playsInline
            preload="metadata"
          />
        ) : isEmbed ? (
          <div className="aspect-video">
            <iframe
              src={FOUNDER_VIDEO_URL}
              title="Watch this before you apply — Sean Mills, Revolution Travel"
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="flex aspect-video flex-col items-center justify-center px-6 text-center text-white">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-revolution-orange shadow-lg">
              <svg className="ml-1 h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            <p className="mt-4 font-semibold">Founder video coming soon</p>
            <p className="mt-1 text-sm text-blue-200">
              A short message from Sean on who this opportunity is — and isn&rsquo;t — for.
            </p>
            <p className="mt-1 text-xs text-blue-200/80">
              Why loving travel alone is not enough to succeed in this business.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelLanding() {
  useEffect(() => {
    captureAttribution();
    trackEvent('landing_page_view');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page bg-white">
      <Header />

      {/* ---------------------------------------------------------------- Hero */}
      <section className="relative overflow-hidden bg-revolution-navy text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('/Scarlet_Night.jpg')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-br from-revolution-navy via-revolution-navy/95 to-revolution-navy/70"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-28">
          <p className="mb-5 inline-block rounded-full border border-white/25 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-orange-300">
            Revolution Travel · Entrepreneur Application
          </p>
          <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">
            Build a Luxury Travel Business With Revolution Travel
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-blue-100 sm:text-xl">
            This is not a traditional travel job. It&rsquo;s a selective business opportunity for
            entrepreneurial people who want to build something real in the luxury travel industry.
          </p>
          <p className="mt-4 text-sm font-semibold text-orange-200 sm:text-base">
            Built by an award-winning Canadian travel entrepreneur.
          </p>
          <div className="mt-9">
            <PrimaryCTA className="w-full sm:w-auto" location="hero">Apply to See If You&rsquo;re a Fit</PrimaryCTA>
          </div>
          <p className="mt-4 text-sm font-semibold text-orange-200">
            {COHORT.deadline}
          </p>
          <p className="mx-auto mt-3 max-w-xl text-sm text-blue-200">
            We intentionally keep our advisor network selective and growth-focused. Not every
            applicant is accepted.
          </p>
        </div>
      </section>

      {/* ------------------------------------- NOT for casual travel lovers */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <p className="text-sm font-bold uppercase tracking-widest text-revolution-orange">
            Read this first
          </p>
          <h2 className="mt-3 text-3xl font-extrabold text-revolution-navy sm:text-4xl">
            This is NOT for casual travel lovers
          </h2>
          <p className="mt-5 text-lg text-gray-600">
            Success here has nothing to do with how much you like vacations. It is about building
            relationships, generating your own leads, following up consistently, learning how to
            sell, and serving clients professionally. Revolution Travel is a commission-based
            business opportunity — travel is the product, but entrepreneurship is the opportunity.
          </p>
          <figure className="mx-auto mt-10 max-w-2xl border-l-4 border-revolution-orange bg-revolution-gray/60 px-6 py-6 text-left sm:px-8 sm:py-7">
            <blockquote className="text-2xl font-extrabold leading-snug text-revolution-navy sm:text-3xl">
              &ldquo;Loving travel is not enough. Building a business is the job.&rdquo;
            </blockquote>
          </figure>
        </div>
      </section>

      {/* ------------------------------------------------ Who we're looking for */}
      <section className="bg-revolution-gray py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-revolution-navy sm:text-4xl">
              Who We&rsquo;re Looking For
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              We partner with a specific kind of person. If this sounds like you, you should apply.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {lookingFor.map((item) => (
              <div key={item.title} className="rounded-xl bg-white p-7 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                    <CheckIcon />
                  </span>
                  <div>
                    <h3 className="text-lg font-bold text-revolution-navy">{item.title}</h3>
                    <p className="mt-1 text-gray-600">{item.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- Who this is not for */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-revolution-navy sm:text-4xl">
              Who This Is Not For
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              We would rather be upfront. This is not the right opportunity if&hellip;
            </p>
          </div>
          <div className="mt-10 space-y-4">
            {notFor.map((item) => (
              <div
                key={item}
                className="flex items-start gap-4 rounded-xl border border-red-100 bg-red-50 p-5"
              >
                <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-red-500">
                  <CrossIcon />
                </span>
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------- Income reality */}
      <section className="bg-revolution-gray py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-revolution-orange">
              An honest look
            </p>
            <h2 className="mt-3 text-3xl font-extrabold text-revolution-navy sm:text-4xl">
              What It Actually Takes to Succeed
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              This is a real business opportunity, so we will set expectations honestly before you
              apply.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-green-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-bold text-revolution-navy">
                Successful advisors usually have&hellip;
              </h3>
              <ul className="mt-4 space-y-3">
                {successfulHave.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex-shrink-0">
                      <CheckIcon />
                    </span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-red-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-bold text-revolution-navy">
                People tend to struggle when&hellip;
              </h3>
              <ul className="mt-4 space-y-3">
                {peopleStruggle.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-500">
                      <CrossIcon />
                    </span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- What you'll build */}
      <section className="bg-revolution-navy py-16 text-white sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold sm:text-4xl">What You&rsquo;ll Build</h2>
            <p className="mt-4 text-lg text-blue-100">
              A flexible, commission-based travel business — backed by mentorship, systems, and an
              established luxury brand.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whatYouBuild.map((item) => (
              <div key={item.title} className="rounded-xl bg-white/5 p-6 ring-1 ring-white/10">
                <h3 className="text-lg font-bold text-orange-300">{item.title}</h3>
                <p className="mt-2 text-sm text-blue-100">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- Meet your mentor */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            {/* Award imagery */}
            <div className="relative mx-auto w-full max-w-md lg:mx-0">
              <img
                src="/Top100.jpeg"
                alt="Sean Mills recognized as a Top 100 North American travel advisor"
                className="aspect-square w-full rounded-2xl object-cover shadow-xl"
              />
              <img
                src="/sean_royal.jpg"
                alt="Sean Mills receiving Royal Caribbean's Best Growth award"
                className="absolute -bottom-8 -right-5 hidden w-2/5 rounded-2xl object-cover shadow-2xl ring-4 ring-white sm:block"
              />
              <div className="absolute -left-5 -top-5 flex h-24 w-24 flex-col items-center justify-center rounded-full bg-revolution-orange text-center text-white shadow-lg">
                <span className="text-xl font-extrabold leading-none">3x</span>
                <span className="mt-0.5 text-[10px] font-bold uppercase leading-tight tracking-wide">
                  Top 100
                  <br />
                  Advisor
                </span>
              </div>
            </div>

            {/* Founder story */}
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-revolution-orange">
                Meet your mentor
              </p>
              <h2 className="mt-3 text-3xl font-extrabold text-revolution-navy sm:text-4xl">
                Built by an Award-Winning Travel Entrepreneur
              </h2>
              <p className="mt-5 text-lg text-gray-600">
                Six years ago, Sean Mills started Revolution Travel with one goal: to build a
                business that created more freedom, more purpose, and a better way to help people
                experience the world.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                In January 2020, after being laid off from CIBC, Sean found himself at a crossroads.
                Instead of stepping back, he decided to go all-in.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                Since then, Revolution Travel has grown into an award-winning luxury and
                cruise-focused business recognized by some of the biggest names in travel.
              </p>
            </div>
          </div>

          {/* Founder credibility — proof points */}
          <div className="mt-14 sm:mt-20">
            <p className="text-center text-sm font-bold uppercase tracking-widest text-revolution-orange">
              Recognized authority
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {founderProofPoints.map((point) => (
                <div
                  key={point.title}
                  className="flex flex-col items-center rounded-xl border border-gray-200 bg-white px-4 py-6 text-center shadow-sm"
                >
                  <p className="text-lg font-extrabold text-revolution-navy">{point.title}</p>
                  <p className="mt-2 text-sm text-gray-600">{point.subtitle}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Watch this before you apply */}
          <FounderVideo />
        </div>
      </section>

      {/* ------------------------------------------ How the application works */}
      <section className="bg-revolution-gray py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-3xl font-extrabold text-revolution-navy sm:text-4xl">
            How the Application Works
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            We review every application. The next step is only unlocked for qualified applicants.
          </p>
          <ol className="mt-12 space-y-4">
            {applicationSteps.map((step, i) => (
              <li
                key={step.title}
                className="flex items-start gap-4 rounded-xl bg-white p-5 shadow-sm"
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-revolution-blue text-lg font-extrabold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="font-bold text-revolution-navy">{step.title}</h3>
                  <p className="mt-0.5 text-gray-600">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ------------------------------------------- Cohort onboarding */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-revolution-orange">
              Intentional onboarding
            </p>
            <h2 className="mt-3 text-3xl font-extrabold text-revolution-navy sm:text-4xl">
              Your First 90 Days Matter
            </h2>
            <p className="mt-5 text-lg text-gray-600">
              We intentionally onboard new advisors in focused quarterly groups so we can properly
              guide each person through their critical first 90 days of building the business.
            </p>
            <p className="mt-4 text-lg text-gray-600">
              This isn&rsquo;t about bringing in as many people as possible. It&rsquo;s about working
              closely with the right people and helping them build real momentum from the beginning.
            </p>
          </div>
          <div className="mx-auto mt-10 max-w-xl rounded-2xl border border-revolution-navy/10 bg-revolution-gray/60 p-6 text-center shadow-sm sm:p-8">
            <p className="text-xs font-bold uppercase tracking-widest text-revolution-orange">
              Next cohort
            </p>
            <p className="mt-2 text-xl font-extrabold text-revolution-navy sm:text-2xl">
              {COHORT.nextCohort}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Selective by design — quality over quantity.
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- Final CTA */}
      <section className="relative overflow-hidden bg-revolution-navy py-16 text-white sm:py-20">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: "url('/Prima.jpeg')" }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="text-3xl font-extrabold sm:text-4xl">
            Ready to find out if you&rsquo;re a fit?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            If you&rsquo;re entrepreneurial, ambitious, and looking for more ownership, freedom, and
            purpose, this may be the opportunity you&rsquo;ve been looking for.
          </p>
          <p className="mt-3 text-base text-blue-200">
            Take 5 minutes to apply. Qualified applicants are invited to take the next step.
          </p>
          <div className="mt-8">
            <PrimaryCTA className="w-full sm:w-auto" location="final">Apply to See If You&rsquo;re a Fit</PrimaryCTA>
          </div>
          <p className="mt-4 text-sm font-semibold text-orange-200">
            {COHORT.deadline}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FunnelLanding;
